import "./RaceDetailsPanel.css";
import React, { useMemo } from "react";
import { IRpgRace } from "../../models/IRpgRace";
import { IRpgAttributes } from "../../models/IRpgAttributes";
import { getAbilityDisplayName } from "../../pages/Characters/characterHelpers";

interface IRaceDetailsPanelProps {
    race: IRpgRace;
}

const formatSigned = (num: number): string => {
    if (num > 0) {
        return `+${num}`;
    }

    return `${num}`;
};

const buildAttributeBullets = (attrs?: IRpgAttributes): string[] => {
    if (!attrs) {
        return [];
    }

    const bullets: string[] = [];

    if (attrs.StatMods) {
        Object.entries(attrs.StatMods).forEach(([key, value]) => {
            if (typeof value !== "number" || value === 0) {
                return;
            }

            bullets.push(`${getAbilityDisplayName(key)}: ${formatSigned(value)}`);
        });
    }

    if (typeof attrs.Speed === "number") {
        bullets.push(`Speed: ${attrs.Speed} ft`);
    }

    if (attrs.Senses && attrs.Senses.length > 0) {
        attrs.Senses.forEach((s) => bullets.push(`Senses: ${s}`));
    }

    if (attrs.Languages && attrs.Languages.length > 0) {
        bullets.push(`Languages: ${attrs.Languages.join(", ")}`);
    }

    if (attrs.Proficiencies && attrs.Proficiencies.length > 0) {
        bullets.push(`Proficiencies: ${attrs.Proficiencies.join(", ")}`);
    }

    if (attrs.Traits && attrs.Traits.length > 0) {
        attrs.Traits.forEach((t) => bullets.push(`Trait: ${t}`));
    }

    return bullets;
};

const RaceDetailsPanel: React.FC<IRaceDetailsPanelProps> = ({ race }) => {
    const bonusBullets = useMemo(() => buildAttributeBullets(race.Bonuses), [race.Bonuses]);
    const negativeBullets = useMemo(
        () => buildAttributeBullets(race.Negatives),
        [race.Negatives]
    );

    const showNegatives = negativeBullets.length > 0;

    return (
        <div className="race-details">
            <h2 className="race-details__title">{race.Name}</h2>

            <div className="race-details__desc">
                <p>{race.LongDescription || race.Description}</p>
            </div>

            <div className="race-details__section">
                <div className="race-details__section-title">Key Attributes</div>

                <div className={`race-details__columns${showNegatives ? " has-negatives" : ""}`}>
                    <div className="race-details__col">
                        <div className="race-details__col-title">Bonuses</div>
                        <ul className="race-details__list">
                            {bonusBullets.map((b) => (
                                <li key={b}>{b}</li>
                            ))}
                        </ul>
                    </div>

                    {showNegatives ? (
                        <div className="race-details__col">
                            <div className="race-details__col-title">Negatives</div>
                            <ul className="race-details__list">
                                {negativeBullets.map((b) => (
                                    <li key={b}>{b}</li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default RaceDetailsPanel;
